import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';

const N = 160;
const MESSAGES = ['谢谢你', '每天都元气满满', '想你啦', '要开心呀', '好运常在', '万事胜意', '平安喜乐', '晚安好梦', '今天也要加油', '喜欢你们'];
const PALETTE = ['#e9a8b6', '#e7ca7f', '#9fc7ae', '#9db9dd', '#e4b18f', '#c2a4cd', '#e2d09f', '#a9c5d8'];
const RINGS = [
  { count: 64, radius: 1.00, z: 1.15, scale: 1.00 },
  { count: 54, radius: 0.79, z: 0.00, scale: 0.93 },
  { count: 42, radius: 0.58, z: -1.10, scale: 0.86 },
];
const STAGGER = 0.030;
const FLY_IN = 0.72;
const HOLD = 1.15;
const ASSEMBLY_END = (N - 1) * STAGGER + FLY_IN;
const BURST_START = ASSEMBLY_END + HOLD;
const SCATTER_START = BURST_START + 0.38;
const SCATTER_DURATION = 2.15;
const FINAL_TIME = SCATTER_START + SCATTER_DURATION + 0.35;
const reducedMotion = new URLSearchParams(window.location.search).get('motion') !== 'full'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const canvas = document.getElementById('webgl');
const loader = document.getElementById('loader');
const loaderProgress = document.getElementById('loader-progress');
const intro = document.getElementById('intro');
const replayButton = document.getElementById('replay');
const steps = Array.from(document.querySelectorAll('.step'));
const errorPanel = document.getElementById('error');

let renderer;
let scene;
let camera;
let bodyMesh;
let cardGeometry;
let cardSize = new THREE.Vector3(1.24, 0.82, 0.085);
let textGeometry;
let textMeshes = [];
let cards = [];
let starField = null;
let startTime = null;
let hiddenAt = 0;
let currentPhase = '';
let lastW = window.innerWidth;
let lastH = window.innerHeight;
let resizeTimer = 0;
let ready = false;
let viewWidth = 30;
let viewHeight = 24;
let cameraDistance = 36;
const dummy = new THREE.Object3D();
const clockVector = new THREE.Vector3();

function mulberry32(seed) {
  return function random() {
    let t = seed += 0x6D2B79F5;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}

function clamp01(v) { return Math.max(0, Math.min(1, v)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }
function easeOutQuart(t) { return 1 - Math.pow(1 - t, 4); }
function easeOutQuint(t) { return 1 - Math.pow(1 - t, 5); }
function easeOutBack(t) {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
}
function heartXY(t) {
  return {
    x: 16 * Math.pow(Math.sin(t), 3),
    y: 13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t),
  };
}

function buildCardData() {
  const random = mulberry32(20261024);
  cards = [];
  let index = 0;
  RINGS.forEach((ring, ringIndex) => {
    for (let i = 0; i < ring.count; i += 1) {
      const t = Math.PI * 2 * i / ring.count;
      const p = heartXY(t);
      const before = heartXY(t - 0.015);
      const after = heartXY(t + 0.015);
      cards.push({
        index,
        ringIndex,
        t,
        ring,
        hx: p.x * ring.radius * 0.61,
        hy: p.y * ring.radius * 0.61 + 0.30,
        tangent: Math.atan2(after.y - before.y, after.x - before.x),
        baseScale: ring.scale * 1.16 * (0.96 + random() * 0.08),
        messageIndex: index % MESSAGES.length,
        paletteIndex: (index + ringIndex * 3) % PALETTE.length,
        textIndex: -1,
        textMesh: null,
        r: Array.from({ length: 14 }, () => random()),
      });
      index += 1;
    }
  });
}

function createPaperTexture() {
  const size = 256;
  const paper = document.createElement('canvas');
  paper.width = size;
  paper.height = size;
  const ctx = paper.getContext('2d');
  const image = ctx.createImageData(size, size);
  const random = mulberry32(4412);
  for (let i = 0; i < image.data.length; i += 4) {
    const value = 118 + Math.floor(random() * 24);
    image.data[i] = value;
    image.data[i + 1] = value;
    image.data[i + 2] = value;
    image.data[i + 3] = 255;
  }
  ctx.putImageData(image, 0, 0);
  ctx.globalAlpha = 0.12;
  for (let i = 0; i < 430; i += 1) {
    const shade = random() > 0.5 ? 180 : 70;
    ctx.strokeStyle = `rgb(${shade},${shade},${shade})`;
    ctx.lineWidth = 0.35 + random() * 0.45;
    const x = random() * size;
    const y = random() * size;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + 3 + random() * 10, y + (random() - 0.5) * 2);
    ctx.stroke();
  }
  const texture = new THREE.CanvasTexture(paper);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(2.2, 1.5);
  texture.anisotropy = Math.min(4, renderer.capabilities.getMaxAnisotropy());
  return texture;
}

function createTextTexture(message, index) {
  const surface = document.createElement('canvas');
  surface.width = 1024;
  surface.height = 640;
  const ctx = surface.getContext('2d');
  ctx.clearRect(0, 0, surface.width, surface.height);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const ink = '#4c3943';
  const softInk = 'rgba(76,57,67,.54)';

  ctx.fillStyle = softInk;
  ctx.font = '500 23px -apple-system, BlinkMacSystemFont, "PingFang SC", sans-serif';
  ctx.fillText('A  L I T T L E  W I S H', 512, 142);

  ctx.fillStyle = ink;
  const length = Array.from(message).length;
  const fontSize = length >= 7 ? 105 : length >= 6 ? 116 : 128;
  ctx.font = `600 ${fontSize}px -apple-system, BlinkMacSystemFont, "PingFang SC", "Hiragino Sans GB", sans-serif`;
  ctx.fillText(message, 512, 310);

  ctx.strokeStyle = 'rgba(111,78,91,.42)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(472, 408);
  ctx.lineTo(552, 408);
  ctx.stroke();

  ctx.fillStyle = softInk;
  ctx.font = '400 20px -apple-system, BlinkMacSystemFont, sans-serif';
  ctx.fillText(`FOR YOU  ·  ${String(index + 1).padStart(2, '0')}`, 512, 486);

  const texture = new THREE.CanvasTexture(surface);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  return texture;
}

function setupLights() {
  const pmrem = new THREE.PMREMGenerator(renderer);
  const room = new RoomEnvironment();
  const environment = pmrem.fromScene(room, 0.04);
  scene.environment = environment.texture;
  scene.environmentIntensity = 0.36;
  pmrem.dispose();

  const key = new THREE.DirectionalLight(0xffe1d7, 1.65);
  key.position.set(-7, 9, 14);
  scene.add(key);
  const fill = new THREE.DirectionalLight(0xb9c9ff, 0.70);
  fill.position.set(9, -3, 10);
  scene.add(fill);
  const rim = new THREE.DirectionalLight(0xf0a2b8, 0.55);
  rim.position.set(0, 7, -8);
  scene.add(rim);
  scene.add(new THREE.AmbientLight(0xd9d5ed, 0.18));
}

function setupCardMeshes(gltf) {
  let sourceMesh = null;
  gltf.scene.traverse((node) => {
    if (!sourceMesh && node.isMesh && node.geometry) sourceMesh = node;
  });
  if (!sourceMesh) throw new Error('Card geometry is missing');
  cardGeometry = sourceMesh.geometry.clone();
  cardGeometry.computeBoundingBox();
  const initialSize = new THREE.Vector3();
  cardGeometry.boundingBox.getSize(initialSize);
  if (initialSize.y < initialSize.z * 0.35 && initialSize.z > 0.5) {
    cardGeometry.rotateX(Math.PI / 2);
  }
  cardGeometry.center();
  cardGeometry.computeBoundingBox();
  cardGeometry.boundingBox.getSize(cardSize);
  cardGeometry.computeBoundingSphere();

  const paperMaterial = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    roughness: 0.86,
    metalness: 0,
    bumpMap: createPaperTexture(),
    bumpScale: 0.014,
    envMapIntensity: 0.44,
  });
  bodyMesh = new THREE.InstancedMesh(cardGeometry, paperMaterial, N);
  bodyMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  bodyMesh.frustumCulled = false;
  scene.add(bodyMesh);

  textGeometry = new THREE.PlaneGeometry(cardSize.x * 0.88, cardSize.y * 0.88);
  textGeometry.translate(0, 0, cardSize.z / 2 + 0.008);

  const counts = MESSAGES.map((_, messageIndex) => cards.filter(card => card.messageIndex === messageIndex).length);
  textMeshes = MESSAGES.map((message, messageIndex) => {
    const material = new THREE.MeshBasicMaterial({
      map: createTextTexture(message, messageIndex),
      transparent: true,
      alphaTest: 0.025,
      depthWrite: false,
      side: THREE.DoubleSide,
      toneMapped: false,
    });
    const mesh = new THREE.InstancedMesh(textGeometry, material, counts[messageIndex]);
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    mesh.frustumCulled = false;
    mesh.renderOrder = 2;
    scene.add(mesh);
    return mesh;
  });

  const cursors = new Array(MESSAGES.length).fill(0);
  cards.forEach((card) => {
    card.textMesh = textMeshes[card.messageIndex];
    card.textIndex = cursors[card.messageIndex];
    cursors[card.messageIndex] += 1;
    bodyMesh.setColorAt(card.index, new THREE.Color(PALETTE[card.paletteIndex]));
  });
  bodyMesh.instanceColor.needsUpdate = true;
}

function createStars() {
  if (starField) {
    scene.remove(starField);
    starField.geometry.dispose();
    starField.material.dispose();
  }
  const random = mulberry32(8008);
  const positions = new Float32Array(240 * 3);
  for (let i = 0; i < 240; i += 1) {
    positions[i * 3] = (random() - 0.5) * viewWidth * 1.35;
    positions[i * 3 + 1] = (random() - 0.5) * viewHeight * 1.35;
    positions[i * 3 + 2] = -8 - random() * 18;
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  const material = new THREE.PointsMaterial({
    color: 0xc0afd3,
    size: 0.045,
    transparent: true,
    opacity: 0.42,
    depthWrite: false,
    sizeAttenuation: true,
  });
  starField = new THREE.Points(geometry, material);
  scene.add(starField);
}

function computeLayout() {
  const width = Math.max(1, window.innerWidth);
  const height = Math.max(1, window.innerHeight);
  const aspect = width / height;
  const fovRadians = THREE.MathUtils.degToRad(camera.fov / 2);
  const fitHeight = 18.0 / 0.78;
  const fitWidth = 20.4 / 0.90;
  cameraDistance = Math.max(fitHeight / (2 * Math.tan(fovRadians)), fitWidth / (2 * Math.tan(fovRadians) * aspect));
  cameraDistance = Math.max(28, cameraDistance);
  camera.position.set(0, 0, cameraDistance);
  camera.aspect = aspect;
  camera.updateProjectionMatrix();
  renderer.setSize(width, height, false);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, width < 700 ? 1.6 : 1.85));

  viewHeight = 2 * cameraDistance * Math.tan(fovRadians);
  viewWidth = viewHeight * aspect;
  const random = mulberry32(7319);
  const columns = Math.max(4, Math.round(Math.sqrt(N * aspect)));
  const rows = Math.ceil(N / columns);
  const cells = Array.from({ length: columns * rows }, (_, i) => i);
  for (let i = cells.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [cells[i], cells[j]] = [cells[j], cells[i]];
  }
  const marginX = viewWidth * 0.09;
  const marginTop = viewHeight * (width < 640 ? 0.12 : 0.10);
  const marginBottom = viewHeight * (width < 640 ? 0.16 : 0.15);
  const availableW = viewWidth - marginX * 2;
  const availableH = viewHeight - marginTop - marginBottom;
  const cellW = availableW / columns;
  const cellH = availableH / rows;
  const finalBoost = width < 640 ? 1.62 : 1.36;

  cards.forEach((card) => {
    const r = card.r;
    card.heart = new THREE.Vector3(card.hx, card.hy, card.ring.z);
    card.heartRot = new THREE.Euler(
      THREE.MathUtils.degToRad((r[0] - 0.5) * 18),
      THREE.MathUtils.degToRad((r[1] - 0.5) * 20),
      card.tangent + (r[2] - 0.5) * 0.20,
    );
    const radial = new THREE.Vector3(card.hx, card.hy - 0.5, 0);
    if (radial.lengthSq() < 0.02) radial.set(r[3] - 0.5, 0.6, 0);
    radial.normalize();
    card.entry = card.heart.clone().addScaledVector(radial, 6 + r[3] * 7);
    card.entry.z = -7 - r[4] * 8;
    card.entryRot = new THREE.Euler(card.heartRot.x + (r[5] - 0.5) * 2.2, card.heartRot.y + (r[6] - 0.5) * 2.2, card.heartRot.z + (r[7] - 0.5) * 3.5);
    card.burst = card.heart.clone().addScaledVector(radial, 1.8 + r[8] * 2.3);
    card.burst.z += 0.5 + r[9] * 3.2;
    card.burstRot = new THREE.Euler(card.heartRot.x + (r[10] - 0.5) * 0.8, card.heartRot.y + (r[11] - 0.5) * 0.8, card.heartRot.z + (r[12] - 0.5) * 1.3);

    const cell = cells[card.index];
    const col = cell % columns;
    const row = Math.floor(cell / columns);
    const sx = -viewWidth / 2 + marginX + (col + 0.5) * cellW + (r[3] - 0.5) * cellW * 0.86;
    const sy = viewHeight / 2 - marginTop - (row + 0.5) * cellH + (r[4] - 0.5) * cellH * 0.82;
    const sz = -1.6 + r[5] * 4.0;
    card.scatter = new THREE.Vector3(sx, sy, sz);
    card.scatterRot = new THREE.Euler(
      THREE.MathUtils.degToRad((r[6] - 0.5) * 62),
      THREE.MathUtils.degToRad((r[7] - 0.5) * 68),
      (r[8] - 0.5) * Math.PI * 0.72,
    );
    card.finalScale = card.baseScale * finalBoost * (0.92 + r[9] * 0.18);
    card.burstDelay = r[10] * 0.14;
    card.scatterDelay = r[11] * 0.26;
    card.floatPhase = r[12] * Math.PI * 2;
    card.floatSpeed = 0.55 + r[13] * 0.55;
  });
  createStars();
}

function setPhase(phase) {
  if (currentPhase === phase) return;
  currentPhase = phase;
  const order = ['gather', 'burst', 'float'];
  const activeIndex = order.indexOf(phase);
  steps.forEach((step) => {
    const index = order.indexOf(step.dataset.step);
    step.classList.toggle('active', index === activeIndex);
    step.classList.toggle('done', index < activeIndex);
  });
  intro.classList.toggle('away', phase !== 'gather');
  replayButton.classList.toggle('visible', phase === 'float');
}

function interpolateEuler(target, a, b, t) {
  target.set(lerp(a.x, b.x, t), lerp(a.y, b.y, t), lerp(a.z, b.z, t));
}

function updateCards(elapsed) {
  cards.forEach((card) => {
    let scale = 0.001;
    const arrivalDelay = card.index * STAGGER;
    const arrivalT = clamp01((elapsed - arrivalDelay) / FLY_IN);

    if (elapsed < BURST_START) {
      if (elapsed < arrivalDelay) {
        dummy.position.copy(card.entry);
        dummy.rotation.copy(card.entryRot);
        scale = 0.001;
      } else if (arrivalT < 1) {
        const moveT = easeOutCubic(arrivalT);
        dummy.position.lerpVectors(card.entry, card.heart, moveT);
        interpolateEuler(dummy.rotation, card.entryRot, card.heartRot, moveT);
        scale = Math.max(0.001, easeOutBack(arrivalT)) * card.baseScale;
      } else {
        const breathe = reducedMotion ? 0 : Math.sin(elapsed * 1.7 + card.floatPhase) * 0.025;
        dummy.position.copy(card.heart);
        dummy.position.z += breathe;
        dummy.rotation.copy(card.heartRot);
        scale = card.baseScale * (1 + breathe * 0.15);
      }
    } else if (elapsed < SCATTER_START + card.scatterDelay) {
      const burstT = clamp01((elapsed - BURST_START - card.burstDelay) / 0.48);
      const moveT = easeOutQuart(burstT);
      dummy.position.lerpVectors(card.heart, card.burst, moveT);
      interpolateEuler(dummy.rotation, card.heartRot, card.burstRot, moveT);
      scale = card.baseScale * (1 + Math.sin(moveT * Math.PI) * 0.22);
    } else {
      const scatterT = clamp01((elapsed - SCATTER_START - card.scatterDelay) / SCATTER_DURATION);
      if (scatterT < 1) {
        const moveT = easeOutQuint(scatterT);
        dummy.position.lerpVectors(card.burst, card.scatter, moveT);
        dummy.position.z += Math.sin(moveT * Math.PI) * (1.5 + card.r[0] * 2.2);
        dummy.position.y += Math.sin(moveT * Math.PI) * 0.35;
        interpolateEuler(dummy.rotation, card.burstRot, card.scatterRot, moveT);
        scale = lerp(card.baseScale, card.finalScale, moveT);
      } else {
        const time = reducedMotion ? 0 : elapsed - FINAL_TIME;
        const phase = card.floatPhase;
        const speed = card.floatSpeed;
        dummy.position.copy(card.scatter);
        dummy.position.x += Math.sin(time * speed * 0.64 + phase) * 0.18;
        dummy.position.y += Math.sin(time * speed + phase * 1.3) * 0.22;
        dummy.position.z += Math.cos(time * speed * 0.52 + phase) * 0.14;
        dummy.rotation.set(
          card.scatterRot.x + Math.sin(time * 0.45 + phase) * 0.055,
          card.scatterRot.y + Math.cos(time * 0.38 + phase) * 0.065,
          card.scatterRot.z + Math.sin(time * 0.30 + phase) * 0.045,
        );
        scale = card.finalScale;
      }
    }

    dummy.scale.setScalar(scale);
    dummy.updateMatrix();
    bodyMesh.setMatrixAt(card.index, dummy.matrix);
    card.textMesh.setMatrixAt(card.textIndex, dummy.matrix);
  });
  bodyMesh.instanceMatrix.needsUpdate = true;
  textMeshes.forEach(mesh => { mesh.instanceMatrix.needsUpdate = true; });
}

function getDebugElapsed() {
  const phase = new URLSearchParams(window.location.search).get('phase');
  if (phase === 'heart') return ASSEMBLY_END + HOLD * 0.65;
  if (phase === 'burst') return BURST_START + 0.24;
  if (phase === 'scatter') return FINAL_TIME + 1;
  return null;
}

function animate(now) {
  requestAnimationFrame(animate);
  if (!ready) return;
  if (startTime === null) startTime = now + 250;
  const debugElapsed = getDebugElapsed();
  const elapsed = reducedMotion ? FINAL_TIME + 1 : debugElapsed !== null ? debugElapsed : Math.max(0, (now - startTime) / 1000);
  if (elapsed < BURST_START) setPhase('gather');
  else if (elapsed < SCATTER_START + 0.7) setPhase('burst');
  else setPhase('float');
  updateCards(elapsed);
  if (starField && !reducedMotion) starField.rotation.z = Math.sin(elapsed * 0.035) * 0.025;
  renderer.render(scene, camera);
}

function replay() {
  if (!ready) return;
  const url = new URL(window.location.href);
  if (url.searchParams.has('phase')) {
    url.searchParams.delete('phase');
    window.history.replaceState({}, '', url);
  }
  startTime = null;
  replayButton.classList.remove('visible');
  intro.classList.remove('away');
  intro.classList.add('visible');
  currentPhase = '';
}

function showFallback() {
  errorPanel.hidden = false;
  window.setTimeout(() => window.location.replace('./fallback.html'), 1100);
}

async function init() {
  try {
    loaderProgress.style.width = '18%';
    renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, powerPreference: 'high-performance' });
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 0.95;
    renderer.setClearColor(0x000000, 0);
    scene = new THREE.Scene();
    camera = new THREE.PerspectiveCamera(38, window.innerWidth / window.innerHeight, 0.1, 160);
    setupLights();
    buildCardData();
    computeLayout();
    loaderProgress.style.width = '42%';

    const manager = new THREE.LoadingManager();
    manager.onProgress = (_, loaded, total) => {
      loaderProgress.style.width = `${42 + (loaded / Math.max(1, total)) * 48}%`;
    };
    const gltf = await new GLTFLoader(manager).loadAsync('./assets/card.glb?v=20261024');
    setupCardMeshes(gltf);
    loaderProgress.style.width = '100%';
    ready = true;
    startTime = null;
    updateCards(0);
    renderer.render(scene, camera);
    window.setTimeout(() => {
      loader.classList.add('finished');
      if (getDebugElapsed() === null && !reducedMotion) intro.classList.add('visible');
    }, 280);
    window.__heartReady = true;
    requestAnimationFrame(animate);
  } catch (error) {
    console.error('3D scene could not start:', error);
    showFallback();
  }
}

document.addEventListener('click', (event) => {
  if (!ready || currentPhase !== 'float') return;
  if (event.target.closest && event.target.closest('#app')) replay();
}, true);
canvas.addEventListener('webglcontextlost', (event) => {
  event.preventDefault();
  showFallback();
});
window.addEventListener('resize', () => {
  window.clearTimeout(resizeTimer);
  resizeTimer = window.setTimeout(() => {
    if (!renderer || !camera) return;
    const width = window.innerWidth;
    const height = window.innerHeight;
    if (width === lastW && Math.abs(height - lastH) < 80) {
      lastH = height;
      renderer.setSize(width, height, false);
      return;
    }
    lastW = width;
    lastH = height;
    computeLayout();
  }, 160);
});
document.addEventListener('visibilitychange', () => {
  if (document.hidden) hiddenAt = performance.now();
  else if (hiddenAt && !reducedMotion) {
    if (startTime !== null) startTime += performance.now() - hiddenAt;
    hiddenAt = 0;
  }
});

init();
